<script lang="ts">
    import {
        memoryPageRegions,
        memoryRegionColor,
        readOnlyMemoryAt
    } from '#lib/languages/memoryRegions.ts'
    import type {
        MemoryRegion,
        DataLabel,
        ReadOnlyMemory
    } from '#lib/languages/commonLanguageFeatures.svelte.ts'
    import Button from '#cmp/shared/button/Button.svelte'
    import { tooltip } from '#cmp/shared/floating/tooltip.svelte.ts'
    import Icon from '#cmp/shared/layout/Icon.svelte'
    import ValueDiff from '#cmp/specific/project/user-tools/ValueDiffer.svelte'
    import MdTextFields from '~icons/ic/baseline-text-fields'
    import FaTimes from '~icons/fa-solid/times'
    import FaExclamationTriangle from '~icons/fa-solid/exclamation-triangle'
    import { onMount } from 'svelte'
    import {
        findElInTree,
        getNumberInRange,
        goesNextLineBy,
        inRange,
        isMemoryChunkEqual,
        type MemoryCell,
        type MemoryReading,
        parseMemoryPoke
    } from '#cmp/specific/project/memory/memoryTabUtils.ts'
    import Row from '#cmp/shared/layout/Row.svelte'
    import ContextMenu, { type ContextMenuEntry } from '#cmp/shared/menu/ContextMenu.svelte'
    import {
        type ColorizedLabel,
        type DiffedMemory,
        RegisterSize
    } from '#lib/languages/commonLanguageFeatures.svelte.ts'
    import { unsignedBigIntToSigned } from '#lib/utils.ts'

    const id = Math.random().toString(36).substring(8)

    const DisplayType = {
        Hex: 'Hex',
        Char: 'Char',
        Decimal: 'Decimal'
    } as const

    interface Props {
        memoryRegions?: readonly MemoryRegion[]
        dataLabels?: readonly DataLabel[]
        /** Memory that takes no Pokes: a selection touching it reads as it would in a read only panel. */
        readOnlyMemory?: readonly ReadOnlyMemory[]
        memory: DiffedMemory
        currentAddress: bigint
        callStackAddresses?: ColorizedLabel[]
        sp: bigint
        pageSize: number
        bytesPerRow: number
        style?: string
        defaultMemoryValue: number
        endianess: 'big' | 'little'
        systemSize: RegisterSize
        /**
         * The highest address there is: every address is padded to its digits, so the address
         * column keeps one width whichever page is shown. Without it they pad to four.
         */
        memorySize?: bigint
        /** The Workbench's narrower page: smaller digits in tighter cells. */
        dense?: boolean
        /**
         * Whether the bytes take Pokes ([the design record](../../../../../../../docs/design/pokes.md)):
         * the page's half of the availability rule, which is the Emulator's `canPoke` and the
         * Project being neither read only nor busy. A panel that passes nothing is read only, and
         * its selection popup is the reading it has always been.
         */
        pokeable?: boolean
        /** One commit of the selection, which is one Poke however many bytes it holds. */
        onPoke?: (address: bigint, bytes: Uint8Array) => void
        /**
         * The menu a right click on a byte opens, the Debug session's pointer menu. Without it a
         * right click is the browser's, and selects the byte as any press does.
         */
        cellMenu?: (cell: MemoryCell) => ContextMenuEntry[]
        /** A byte to mark while its page is shown: where a pointer menu last moved the view. */
        markedAddress?: bigint
    }

    let {
        memoryRegions = [],
        dataLabels = [],
        readOnlyMemory = [],
        memory,
        currentAddress,
        sp,
        pageSize,
        bytesPerRow,
        style = '',
        defaultMemoryValue,
        endianess,
        callStackAddresses = [],
        systemSize,
        memorySize,
        dense = false,
        pokeable = false,
        onPoke,
        cellMenu,
        markedAddress
    }: Props = $props()
    const annotations = $derived.by(() => {
        //with each byte's hover, and what a region the machine defines means there: a ROM's
        //writes, a vector's handler
        const cells = memoryPageRegions(memoryRegions, dataLabels, currentAddress, pageSize)
        return cells.map((cell, index) => {
            const region = cell.region
            if (!region) return { ...cell, outline: '' }
            const sameRegion = (neighbor: number) => cells[neighbor]?.region?.id === region.id
            const sameChunk = (neighbor: number) =>
                sameRegion(neighbor) && cells[neighbor].chunk === cell.chunk
            const column = index % bytesPerRow
            // Only the exposed edges of a region's cells form its continuous outline. Inside it,
            // the labels cut the region into chunks in the same colour: the line between two
            // chunks is drawn once, by the later chunk's top and left edges.
            const edges = [
                !sameChunk(index - bytesPerRow),
                column === bytesPerRow - 1 || !sameRegion(index + 1),
                !sameRegion(index + bytesPerRow),
                column === 0 || !sameChunk(index - 1)
            ]
            return {
                ...cell,
                outline: `--region-color: ${memoryRegionColor(region.kind)}; --region-edges: ${edges.map((edge) => (edge ? '1px' : '0')).join(' ')};`
            }
        })
    })
    let memoryGrid = $state<HTMLDivElement>()
    const addressDigits = $derived(memorySize ? memorySize.toString(16).length : 4)
    const selectedAddressesIndexes = $state({
        start: -1,
        len: 0
    })
    let selectingAddresses = $state(false)

    let type = $state(DisplayType.Hex) as (typeof DisplayType)[keyof typeof DisplayType]
    let visibleAddresses = $derived(
        new Array(pageSize / bytesPerRow)
            .fill(0)
            .map((_, i) => currentAddress + BigInt(i * bytesPerRow))
    )

    onMount(() => {
        const deselect = () => {
            selectingAddresses = false
        }
        const selectFn = (e: KeyboardEvent) => {
            if (e.key === 'Escape') clearSelection()
        }
        window.addEventListener('blur', deselect)
        window.addEventListener('keydown', selectFn)
        window.addEventListener('pointerup', deselect)
        return () => {
            window.removeEventListener('blur', deselect)
            window.removeEventListener('keydown', selectFn)
            window.removeEventListener('pointerup', deselect)
        }
    })

    /** Ends the selection, and the drag that may be making one. */
    function clearSelection() {
        selectingAddresses = false
        selectedAddressesIndexes.start = -1
        selectedAddressesIndexes.len = 0
    }

    /**
     * The index of the byte an event happened on: its cell, or what is inside it. The selection
     * popup in a cell is beside the bytes and not on one, so it has none.
     */
    function cellIndexAt(target: EventTarget | null): number | undefined {
        const el = findElInTree(target as HTMLElement, id)
        const index = el ? parseInt(el.id.split('-')[1]) : NaN
        return isNaN(index) ? undefined : index
    }

    function getTextFromValue(
        value: bigint,
        padding?: number,
        typeOverride?: (typeof DisplayType)[keyof typeof DisplayType]
    ) {
        switch (typeOverride ?? type) {
            case DisplayType.Hex:
                return value
                    .toString(16)
                    .padStart(padding ?? 0, '0')
                    .toUpperCase()
            case DisplayType.Char:
                //hides last extended ascii to have prettier view
                return value === BigInt(defaultMemoryValue)
                    ? '.'
                    : String.fromCharCode(Number(value))
            case DisplayType.Decimal:
                return value.toString().padStart(padding ?? 2, '0')
            default:
                return value.toString()
        }
    }

    function onPointerDown(e: PointerEvent) {
        //the other buttons open the menu, and leave the selection and its input as they are
        if (cellMenu && e.button > 0) return
        selectingAddresses = true
        const index = cellIndexAt(e.target)
        if (index === undefined) return
        //clicking away commits, and this is where the click is: the browser moves the selection
        //here and only blurs the input when the pointerdown's default action runs, by which time
        //the effect below has already put the popup back to the bytes
        commitPoke()
        if (selectedAddressesIndexes.start === index && selectedAddressesIndexes.len === 0) {
            clearSelection()
            lastIdx = -1
            return
        }
        selectedAddressesIndexes.start = index
        selectedAddressesIndexes.len = 0
    }

    let lastIdx = -1

    function handlePointerMove(e: PointerEvent) {
        if (!selectingAddresses) return
        const index = cellIndexAt(e.target)
        if (index === undefined) return
        if (lastIdx === index && selectedAddressesIndexes.start !== -1) return
        lastIdx = index
        //a drag moves the selection away from what was typed, which is a commit like any other
        commitPoke()
        if (selectedAddressesIndexes.start === -1) {
            selectedAddressesIndexes.start = index
            selectedAddressesIndexes.len = 0
        } else {
            selectedAddressesIndexes.len = index - selectedAddressesIndexes.start
            if (selectedAddressesIndexes.len < 0) {
                selectedAddressesIndexes.len = Math.max(
                    selectedAddressesIndexes.len,
                    -systemSize + 1
                )
            } else {
                selectedAddressesIndexes.len = Math.min(
                    selectedAddressesIndexes.len,
                    systemSize - 1
                )
            }
            if (selectedAddressesIndexes.len <= -1 || selectedAddressesIndexes.len >= 1) {
                window.getSelection()?.removeAllRanges()
            }
        }
    }

    //the selected run as the panel writes it: the lower of the two ends, since a selection dragged
    //backwards keeps its anchor in `start`, and the count of bytes it covers
    const selectionStart = $derived(
        Math.min(
            selectedAddressesIndexes.start,
            selectedAddressesIndexes.start + selectedAddressesIndexes.len
        )
    )
    const selectionLength = $derived(
        selectedAddressesIndexes.start === -1 ? 0 : Math.abs(selectedAddressesIndexes.len) + 1
    )
    //the number the selection holds, and how far its popup has to move left to stay in its row;
    //read only while there is a selection
    const selectionValue = $derived(
        getNumberInRange(
            memory,
            selectedAddressesIndexes.start,
            selectedAddressesIndexes.len,
            endianess
        )
    )
    const overflowsBy = $derived(
        goesNextLineBy(selectedAddressesIndexes.start, selectedAddressesIndexes.len, bytesPerRow)
    )
    /**
     * The bytes the Core could not read, which hold no value: each is drawn as `??`, the page says
     * why below the grid, and a selection that covers one reads as nothing and takes no Poke.
     */
    const unreadable = $derived(memory.unreadable ?? null)
    const unreadableCount = $derived(
        unreadable ? unreadable.mask.reduce((count, byte) => count + byte, 0) : 0
    )
    const selectionUnreadable = $derived(
        unreadable !== null &&
            selectionLength > 0 &&
            unreadable.mask.subarray(selectionStart, selectionStart + selectionLength).includes(1)
    )
    const selectionReadOnly = $derived(
        selectionLength > 0
            ? readOnlyMemoryAt(
                  readOnlyMemory,
                  currentAddress + BigInt(selectionStart),
                  BigInt(selectionLength)
              )
            : undefined
    )
    const pokeReading: MemoryReading = $derived(
        type === DisplayType.Hex ? 'hex' : type === DisplayType.Char ? 'char' : 'decimal'
    )

    /**
     * The text typed into the selection popup ([the design record](../../../../../../../docs/design/pokes.md)):
     * null while nobody is typing, when the popup's input shows the selection's own reading and a
     * refresh that changes the bytes changes what it holds. `pokeReason` is why the last commit was
     * refused, which keeps what was typed and marks the input.
     */
    let pokeText: string | null = $state(null)
    let pokeReason: string | null = $state(null)
    /**
     * The run the open input belongs to, taken when the first character is typed: clicking another
     * byte moves the selection and only then blurs the input, so a commit that read the selection
     * would land at the byte that was clicked rather than at the one that was typed into.
     */
    let pokeAnchor: { address: bigint; bytes: Uint8Array } | null = $state(null)

    /** The input goes back to showing what memory holds, whatever was being typed into it. */
    function cancelPoke() {
        pokeText = null
        pokeReason = null
        pokeAnchor = null
    }

    //A resized page has different cell positions and may no longer contain the selection.
    $effect(() => {
        void pageSize
        void bytesPerRow
        clearSelection()
        cancelPoke()
    })

    //a new selection, or a new reading of it, is a new value to read, and a Run or a Build taking
    //the Core takes the input away entirely: in every case the popup goes back to the bytes
    $effect(() => {
        void selectedAddressesIndexes.start
        void selectedAddressesIndexes.len
        void pokeable
        void type
        cancelPoke()
    })

    /**
     * How the popup reads one selected run: a single byte in the reading its own cell shows, a
     * longer selection as the number of the run the popup has always shown. A byte in character
     * mode reads as the character itself and not as the dot the grid draws for untouched memory,
     * because the input is committed back as it stands and a dot would poke a `$2E`.
     */
    function selectionReading(value: bigint): string {
        if (selectionLength !== 1) return value.toString()
        switch (type) {
            case DisplayType.Hex:
                return value.toString(16).padStart(2, '0').toUpperCase()
            case DisplayType.Char:
                return String.fromCharCode(Number(value))
            default:
                return value.toString()
        }
    }

    /**
     * Enter, or clicking away, commits the whole selection as one Poke, in the panel's endianness.
     * A commit that leaves the bytes as they were calls nothing, as the design record asks, and one
     * that does not fit the selection keeps the input open with the reason on it rather than
     * writing a truncated value.
     */
    function commitPoke() {
        //nothing was typed since the popup last showed the bytes, so there is nothing to commit
        if (pokeText === null || pokeAnchor === null || !pokeable || !onPoke || selectionReadOnly)
            return
        const anchor = pokeAnchor
        const parsed = parseMemoryPoke(pokeText, anchor.bytes.length, endianess, pokeReading)
        if (!parsed.ok) {
            pokeReason = parsed.reason
            return
        }
        cancelPoke()
        if (isMemoryChunkEqual(anchor.bytes, parsed.bytes)) return
        //the change highlight comes from the refresh the Poke ends with, never from here
        onPoke(anchor.address, parsed.bytes)
    }

    /** The run being typed into, taken as it stands the moment the typing starts. */
    function anchorPoke() {
        if (pokeAnchor !== null || selectionLength <= 0 || selectionUnreadable || selectionReadOnly)
            return
        pokeAnchor = {
            address: currentAddress + BigInt(selectionStart),
            bytes: memory.current.slice(selectionStart, selectionStart + selectionLength)
        }
    }

    function onPokeKey(e: KeyboardEvent) {
        if (e.key === 'Enter') {
            e.preventDefault()
            commitPoke()
        } else if (e.key === 'Escape') {
            //Escape ends the selection from anywhere in the window; while the input has the focus
            //it is the input it cancels, so the selection stays and the bytes can be read again
            e.preventDefault()
            e.stopPropagation()
            cancelPoke()
            ;(e.currentTarget as HTMLInputElement).blur()
        }
    }

    //the pointer menu, at the pointer: a byte takes no focus, so no key opens it
    let contextMenu = $state<{ x: number; y: number; items: ContextMenuEntry[] } | null>(null)
    function onContextMenu(e: MouseEvent) {
        if (!cellMenu) return
        //the input being typed into keeps the browser's own menu, for pasting into it
        if ((e.target as Element | null)?.closest?.('input')) return
        const index = cellIndexAt(e.target)
        if (index === undefined) return
        e.preventDefault()
        const inSelection =
            selectionLength > 0 &&
            index >= selectionStart &&
            index < selectionStart + selectionLength
        const value = inSelection
            ? selectionUnreadable
                ? undefined
                : selectionValue.current
            : unreadable?.mask[index]
              ? undefined
              : BigInt(memory.current[index])
        contextMenu = {
            x: e.clientX,
            y: e.clientY,
            //inside a selection, the cell is the selection: its number, and where it starts, which
            //Follow pointer reads from as the number reads
            items: cellMenu({
                address: currentAddress + BigInt(inSelection ? selectionStart : index),
                value,
                bytes: inSelection ? selectionLength : 1
            })
        }
    }
</script>

<div
    class="memory-grid"
    bind:this={memoryGrid}
    class:dense
    style={`--bytesPerRow: ${bytesPerRow}; ${style}`}
>
    <div class="memory-offsets">
        {#each new Array(bytesPerRow).keys() as offset (offset)}
            <div>
                {getTextFromValue(BigInt(offset), 2, DisplayType.Hex)}
            </div>
        {/each}
    </div>
    <div class="memory-addresses">
        <Row
            padding="0.25rem"
            gap="0.2rem"
            style="height:2rem; min-width: var(--address-header-width); padding-bottom: 0; padding-right: 0.25rem;"
        >
            <Button
                {@attach tooltip(type === DisplayType.Hex ? 'Show as character' : 'Show as hex')}
                aria-label={type === DisplayType.Hex ? 'Show as character' : 'Show as hex'}
                style="padding: 0.2rem; border-radius: 0.35rem; height:100%; width: 100%"
                onClick={() =>
                    (type = type === DisplayType.Hex ? DisplayType.Char : DisplayType.Hex)}
                active={type === DisplayType.Char}
                cssVar="accent2"
            >
                <Icon size={dense ? 0.8 : 1}>
                    <MdTextFields />
                </Icon>
            </Button>
            {#if selectedAddressesIndexes.start !== -1}
                <Button
                    cssVar="green"
                    style="padding: 0.2rem; border-radius: 0.35rem; height:100%; width: 100%"
                    onClick={clearSelection}
                >
                    <Icon size={dense ? 0.8 : 1}>
                        <FaTimes />
                    </Icon>
                </Button>
            {/if}
        </Row>
        {#each visibleAddresses as address (address)}
            <div class="memory-grid-address">
                {getTextFromValue(address, addressDigits, DisplayType.Hex)}
            </div>
        {/each}
    </div>

    <!-- svelte-ignore a11y_no_static_element_interactions -->
    <div
        class="memory-numbers"
        onpointerdown={onPointerDown}
        oncontextmenu={onContextMenu}
        ondragstart={(e) => e.preventDefault()}
        onpointermove={selectingAddresses ? handlePointerMove : undefined}
    >
        {#each memory.current as word, i (currentAddress + BigInt(i))}
            {@const unreadableByte = !!unreadable?.mask[i]}
            {@const signed = unsignedBigIntToSigned(BigInt(word), 1)}
            {@const frameColor = callStackAddresses.find(
                (frame) => currentAddress + BigInt(i) >= frame.sp
            )?.color}
            <div
                class="memory-number"
                class:outlined={!!annotations[i].region}
                class:marked={markedAddress === currentAddress + BigInt(i)}
                style={annotations[i].outline}
            >
                {#if i === selectedAddressesIndexes.start}
                    {@const signedSelection = unsignedBigIntToSigned(
                        selectionValue.current,
                        selectionValue.len
                    )}
                    <div
                        class="selection-value"
                        style={`
								${i > pageSize - bytesPerRow - 1 ? 'bottom' : 'top'}: calc(100% + 0.1rem);
								left: ${overflowsBy.overflows ? `calc(-${overflowsBy.by} * 100%)` : '0'};
								min-width: calc(${selectionValue.len} * 100%);
						`}
                    >
                        {#if selectionUnreadable}
                            <div title={unreadable?.reason}>??</div>
                        {:else if selectionValue.current !== signedSelection}
                            <div style="user-select: all;">
                                {signedSelection}
                            </div>
                        {/if}
                        {#if selectionUnreadable}
                            <!-- nothing to read, and nothing a Poke could be written over -->
                        {:else if pokeable && onPoke && !selectionReadOnly}
                            <!--
                                the popup is the input while the panel takes Pokes: it holds what
                                the selection reads as, and its previous-value line is drawn in the
                                same reading so the two can be compared
                            -->
                            {@const text = pokeText ?? selectionReading(selectionValue.current)}
                            <input
                                class="selection-input"
                                class:refused={pokeReason !== null}
                                title={pokeReason ??
                                    `Poke ${selectionValue.len} byte${selectionValue.len === 1 ? '' : 's'}`}
                                aria-label="Poke memory"
                                style={`width: calc(${Math.max(text.length, 2)}ch + 0.2rem);`}
                                value={text}
                                oninput={(e) => {
                                    anchorPoke()
                                    pokeText = e.currentTarget.value
                                    pokeReason = null
                                }}
                                onkeydown={onPokeKey}
                                onblur={commitPoke}
                                onpointerdown={(e) => e.stopPropagation()}
                            />
                            <div style="color: var(--accent); user-select: all">
                                {selectionReading(selectionValue.prev)}
                            </div>
                        {:else}
                            <!-- a panel that takes Pokes still reads read only memory, saying why -->
                            <div
                                style="user-select: all;"
                                title={pokeable ? selectionReadOnly?.reason : undefined}
                            >
                                {selectionValue.current}
                            </div>
                            <div style="color: var(--accent); user-select: all">
                                {selectionValue.prev}
                            </div>
                        {/if}
                    </div>
                {/if}
                <!-- a byte in hex is always two digits, `0F` and not `F`, as the Hex editor and the
                     selection's popup write it, so that bytes read side by side make the word -->
                <ValueDiff
                    value={unreadableByte ? '??' : getTextFromValue(BigInt(word), 2, type)}
                    id={`${id}-${i}`}
                    diff={unreadableByte
                        ? '??'
                        : getTextFromValue(
                              BigInt(memory.prevState[i] ?? defaultMemoryValue),
                              2,
                              type
                          )}
                    hasSoftDiff={!unreadableByte &&
                        !annotations[i].region &&
                        word !== defaultMemoryValue}
                    hoverBoundary={memoryGrid}
                    style={`padding: 0.3rem var(--cell-pad-x); min-width: calc(var(--cell-pad-x) * 2 + 2ch); height: calc(2ch + 0.65rem);
                    ${unreadableByte ? 'color: var(--hint);' : ''}
                    ${
                        currentAddress + BigInt(i) === sp
                            ? ' background-color: var(--accent2); color: var(--accent2-text);'
                            : 'border-radius: 0;'
                    }
										${
                                            inRange(
                                                i,
                                                selectedAddressesIndexes.start,
                                                selectedAddressesIndexes.len
                                            )
                                                ? 'background-color: var(--green); color: var(--green-text);'
                                                : ''
                                        }
                    ${frameColor ? `color: ${frameColor}` : ''}
								`}
                    monospaced
                >
                    {#snippet hoverValue()}
                        <div>
                            {#if annotations[i].hover}
                                <div class="region-block">
                                    <div class="region-hover">{annotations[i].hover}</div>
                                    {#each annotations[i].reasons as reason (reason)}
                                        <div class="region-reason">{reason}</div>
                                    {/each}
                                </div>
                            {/if}
                            {#if unreadableByte}
                                <div class="byte-value">??</div>
                                <div class="byte-error">{unreadable?.reason}</div>
                            {:else}
                                {#if BigInt(word) !== signed}
                                    <div class="byte-value" style="user-select: all;">
                                        {signed}
                                    </div>
                                {/if}
                                <div class="byte-value" style="user-select: all">
                                    {word}
                                </div>
                            {/if}
                        </div>
                    {/snippet}
                </ValueDiff>
            </div>
        {/each}
    </div>
    {#if unreadable && unreadableCount > 0}
        <div class="memory-unreadable" class:partial={unreadableCount < pageSize} role="status">
            <Icon size={0.9}>
                <FaExclamationTriangle />
            </Icon>
            <span>
                {unreadableCount === pageSize
                    ? 'Nothing on this page can be read'
                    : `${unreadableCount} of ${pageSize} bytes can't be read`}: {unreadable.reason}
            </span>
        </div>
    {/if}
</div>
<!-- beside the grid rather than in it, so that it reads in the page's type and not the bytes' -->
{#if contextMenu}
    <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        items={contextMenu.items}
        arrow
        label="Memory"
        onClose={() => (contextMenu = null)}
    />
{/if}

<style lang="scss">
    //the value fills its cell, so that in a page stretched taller than its rows need, a
    //Playground's, it is centred beside its address and a highlighted byte still covers the cell
    .memory-number {
        position: relative;
        display: flex;
        flex-direction: column;

        &.outlined::after {
            content: '';
            position: absolute;
            inset: 0;
            border-style: solid;
            border-color: var(--region-color);
            border-width: var(--region-edges);
            pointer-events: none;
            z-index: 1;
        }

        /* the byte a pointer menu moved the view to, ringed inside its cell over the region's
           outline so that it reads on any page */
        &.marked::before {
            content: '';
            position: absolute;
            inset: 1px;
            border: 2px solid var(--accent);
            border-radius: 0.2rem;
            pointer-events: none;
            z-index: 2;
        }

        > :global(div:has(> .tooltip-base)) {
            display: flex;
            flex-direction: column;
            flex: 1;
        }

        :global(.tooltip-base) {
            flex: 1;
        }
        :global(.hover-element) {
            --memory-hover-border: color-mix(in srgb, var(--tertiary-text) 28%, var(--tertiary));
            border: 1px solid var(--memory-hover-border);
            pointer-events: none;
        }
    }

    .region-block {
        border-bottom: 1px solid var(--memory-hover-border);
        padding: 0.2rem 0.4rem;
        margin-bottom: 0.2rem;
    }

    .region-hover {
        overflow-wrap: anywhere;
    }

    .region-reason {
        max-width: 16rem;
        margin-top: 0.15rem;
        font-size: 0.85em;
        opacity: 0.85;
        white-space: normal;
    }

    .byte-value {
        padding: 0 0.3rem 0.2rem;
    }

    .byte-error {
        padding: 0.2rem 0.4rem;
        overflow-wrap: anywhere;
    }

    .selection-value {
        background-color: var(--primary);
        color: var(--primary-text);
        position: absolute;
        z-index: 2;
        text-align: center;
        border-radius: 0.3rem;
        box-shadow: 0 0 0.3rem var(--primary);
        padding: 0.3rem;
    }

    //the input the selection popup becomes while it is being poked, in the panel's own monospace
    //so the digits sit where the popup drew them
    .selection-input {
        font-family: monospace;
        font-size: inherit;
        text-align: center;
        align-self: center;
        //`box-sizing: border-box` is global, so the padding is inside the width the popup sets and
        //the text keeps its own `ch` per character. The frame is an outline drawn inside that box
        //rather than a border, which is layout and would squeeze the digits it draws around.
        padding: 0 0.1rem;
        min-width: calc(2ch + 0.2rem);
        outline: solid 0.1rem var(--accent);
        outline-offset: -0.1rem;
        border-radius: 0.2rem;
        background-color: var(--secondary);
        color: var(--secondary-text);
    }

    //a commit that does not fit the selection: the input keeps what was typed and says why
    .refused {
        outline-color: var(--red);
    }

    .memory-grid {
        display: grid;
        position: relative;
        //Keep the table around its cells so a smaller host scrolls the whole table.
        min-width: min-content;
        min-height: min-content;
        font-family: monospace;
        font-size: 1rem;
        grid-template-columns: min-content;
        grid-template-rows: min-content;
        grid-template-areas:
            'b a a a a'
            'b c c c c'
            'b c c c c'
            'b c c c c'
            'b c c c c';
        background-color: var(--tertiary);
        color: var(--tertiary-text);
        //a host that squares its panels, the Workbench's Lines, squares this one too
        border-radius: var(--panel-radius, 0.5rem);
        padding-right: 0.3rem;
        padding-bottom: 0.3rem;
        --cell-pad-x: 0.3rem;
        --address-header-width: 3.8rem;
    }

    //the cells' sizes are in `ch` and follow the smaller font, as the addresses' do; the rest is
    //their padding, and the address column's header, which fits the hex toggle and the selection's
    //clear button side by side so selecting never widens the column
    .dense {
        font-size: 0.875rem;
        --cell-pad-x: 0.25rem;
        --address-header-width: 3.1rem;

        .memory-grid-address {
            padding: 0 0.35rem;
        }
    }

    //why the Core refused bytes on this page, floating over the bottom of the bytes rather than
    //taking a row of its own, so moving to or from such a page never changes the panel's size.
    //It takes no pointer: the bytes under it stay selectable, and while the pointer is over a
    //page that still has readable bytes it fades so the row it covers can be read
    .memory-unreadable {
        position: absolute;
        z-index: 1;
        left: 0.6rem;
        right: 0.6rem;
        bottom: 0.6rem;
        display: flex;
        gap: 0.4rem;
        align-items: flex-start;
        width: fit-content;
        margin: 0 auto;
        padding: 0.45rem 0.6rem;
        border: 1px solid color-mix(in srgb, var(--red) 45%, transparent);
        border-radius: 0.4rem;
        background: color-mix(in srgb, var(--tertiary) 65%, transparent);
        backdrop-filter: blur(6px);
        box-shadow: 0 0.25rem 0.8rem rgb(0 0 0 / 0.25);
        font-family: sans-serif;
        font-size: 0.85rem;
        color: var(--tertiary-text);
        overflow-wrap: anywhere;
        pointer-events: none;
        transition: opacity 0.15s;

        :global(svg) {
            color: var(--red);
            flex-shrink: 0;
            margin-top: 0.1rem;
        }
    }

    .memory-grid:has(.memory-numbers:hover) .partial {
        opacity: 0.12;
    }

    .memory-numbers {
        grid-area: c;
        color: var(--text-layered);
        display: grid;
        background-color: var(--secondary);
        color: var(--secondary-text);
        border-radius: 0.3rem;
        grid-template-columns: repeat(var(--bytesPerRow), 1fr);
        grid-template-rows: repeat(var(--bytesPerRow), 1fr);
        //a page with more rows than bytes in a row, a Playground's, shares a taller panel evenly
        //between all of them, as the addresses beside them do, and not only between the first
        grid-auto-rows: 1fr;
    }

    .memory-offsets {
        gap: 0.2rem;
        display: flex;
        grid-area: a;
        height: 2rem;
        align-items: center;
        justify-content: space-around;
    }

    .memory-addresses {
        display: flex;
        flex-direction: column;
        grid-area: b;
    }

    .memory-grid-address {
        font-family: monospace;
        padding: 0 0.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 1;
    }
</style>
